import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import EscalationsList from "./EscalationsList";
import {
  ThumbsUp,
  ThumbsDown,
  ExternalLink,
  Loader2,
  Plus,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

type Surface = "member" | "internal" | "public";
type Rating = "up" | "down";
type Status = "open" | "resolved";

interface FeedbackRow {
  id: string;
  surface: Surface;
  rating: Rating;
  question: string;
  answer: string;
  comment: string | null;
  sources: Array<{ doc_id: string; title?: string; collection?: string }>;
  status: Status;
  resolution_note: string | null;
  created_at: string;
  resolved_at: string | null;
}

function surfaceHref(s: { doc_id: string; collection?: string }): string {
  return s.collection === "sop" ? `/support/library/${s.doc_id}` : `/support/library/${s.doc_id}`;
}

function surfaceBadgeVariant(s: Surface) {
  if (s === "internal") return "secondary" as const;
  if (s === "public") return "outline" as const;
  return "default" as const;
}

export default function StewImprovement() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [status, setStatus] = useState<Status>("open");
  const [surfaceFilter, setSurfaceFilter] = useState<"all" | Surface>("all");
  const [ratingFilter, setRatingFilter] = useState<"all" | "down">("down");
  const [rows, setRows] = useState<FeedbackRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [resolveOpen, setResolveOpen] = useState<FeedbackRow | null>(null);
  const [resolveNote, setResolveNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [logSurface, setLogSurface] = useState<Surface>("member");
  const [logQuestion, setLogQuestion] = useState("");
  const [logNote, setLogNote] = useState("");
  const [logComment, setLogComment] = useState("");
  const [topTab, setTopTab] = useState<"feedback" | "escalations">("feedback");

  const [stats, setStats] = useState<{
    open: number;
    resolvedWeek: number;
    bySurface: Record<Surface, number>;
  }>({ open: 0, resolvedWeek: 0, bySurface: { member: 0, internal: 0, public: 0 } });

  const load = async () => {
    setLoading(true);
    let q = supabase
      .from("stew_feedback")
      .select(
        "id, surface, rating, question, answer, comment, sources, status, resolution_note, created_at, resolved_at",
      )
      .eq("status", status)
      .order("created_at", { ascending: false })
      .limit(200);
    if (surfaceFilter !== "all") q = q.eq("surface", surfaceFilter);
    if (ratingFilter === "down") q = q.eq("rating", "down");
    const { data, error } = await q;
    if (error) {
      console.error("StewImprovement load failed:", error);
      toast({
        title: "Feedback list didn't load",
        description: "Refresh the page and try again.",
        variant: "destructive",
      });
    } else {
      setRows((data as any) || []);
    }
    setLoading(false);
  };

  const loadStats = async () => {
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
    const [openRes, resolvedRes, openBySurf] = await Promise.all([
      supabase
        .from("stew_feedback")
        .select("id", { count: "exact", head: true })
        .eq("status", "open"),
      supabase
        .from("stew_feedback")
        .select("id", { count: "exact", head: true })
        .eq("status", "resolved")
        .gte("resolved_at", weekAgo),
      supabase
        .from("stew_feedback")
        .select("surface")
        .eq("status", "open"),
    ]);
    const bySurface: Record<Surface, number> = {
      member: 0,
      internal: 0,
      public: 0,
    };
    ((openBySurf.data as any[]) || []).forEach((r) => {
      if (r.surface in bySurface) bySurface[r.surface as Surface]++;
    });
    setStats({
      open: openRes.count || 0,
      resolvedWeek: resolvedRes.count || 0,
      bySurface,
    });
  };

  useEffect(() => {
    void load();
  }, [status, surfaceFilter, ratingFilter]);

  useEffect(() => {
    void loadStats();
  }, [rows.length]);

  const resolve = async () => {
    if (!resolveOpen || !user) return;
    setBusy(true);
    const { error } = await supabase
      .from("stew_feedback")
      .update({
        status: "resolved",
        resolved_by: user.id,
        resolved_at: new Date().toISOString(),
        resolution_note: resolveNote.trim() || null,
      })
      .eq("id", resolveOpen.id);
    setBusy(false);
    if (error) {
      console.error("StewImprovement resolve failed:", error);
      toast({
        title: "Resolve didn't save",
        description: "Try once more.",
        variant: "destructive",
      });
      return;
    }
    toast({ title: "Resolved. It moves to the Resolved tab." });
    setResolveOpen(null);
    setResolveNote("");
    void load();
  };

  const [reopenBusy, setReopenBusy] = useState<string | null>(null);
  const reopen = async (row: FeedbackRow) => {
    if (reopenBusy) return;
    setReopenBusy(row.id);
    const { error } = await supabase
      .from("stew_feedback")
      .update({
        status: "open",
        resolved_at: null,
        resolved_by: null,
      })
      .eq("id", row.id);
    setReopenBusy(null);
    if (error) {
      console.error("StewImprovement reopen failed:", error);
      toast({
        title: "Reopen didn't save",
        description: "Try once more.",
        variant: "destructive",
      });
      return;
    }
    toast({ title: "Reopened." });
    void load();
  };

  const logImprovement = async () => {
    if (!user || !logQuestion.trim() || !logNote.trim()) return;
    setBusy(true);
    const { error } = await supabase.from("stew_feedback").insert({
      surface: logSurface,
      rating: "down",
      question: logQuestion.trim(),
      answer: "Filed manually by an agent",
      comment: [logNote.trim(), logComment.trim()].filter(Boolean).join("\n\n"),
      sources: [],
      created_by: user.id,
    });
    setBusy(false);
    if (error) {
      console.error("StewImprovement log failed:", error);
      toast({
        title: "That entry didn't file",
        description: "Try once more.",
        variant: "destructive",
      });
      return;
    }
    toast({ title: "Filed. It's in the open queue." });
    setLogOpen(false);
    setLogQuestion("");
    setLogNote("");
    setLogComment("");
    void load();
  };

  const surfaceOptions = useMemo(
    () => [
      { v: "all", l: "All surfaces" },
      { v: "member", l: "Member" },
      { v: "internal", l: "Internal" },
      { v: "public", l: "Public" },
    ],
    [],
  );

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-serif font-semibold">Stew Improvement</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Turn each piece of feedback into a two-minute content fix.
          </p>
        </div>
        <Button onClick={() => setLogOpen(true)} size="sm">
          <Plus className="h-4 w-4 mr-1.5" />
          Log an improvement
        </Button>
      </div>

      <Tabs value={topTab} onValueChange={(v) => setTopTab(v as any)}>
        <TabsList>
          <TabsTrigger value="feedback">Feedback</TabsTrigger>
          <TabsTrigger value="escalations">Escalations</TabsTrigger>
        </TabsList>
        <TabsContent value="escalations" className="mt-4">
          <EscalationsList />
        </TabsContent>
        <TabsContent value="feedback" className="mt-4 space-y-6">


      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">Open</div>
          <div className="text-2xl font-semibold">{stats.open}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">Resolved this week</div>
          <div className="text-2xl font-semibold">{stats.resolvedWeek}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">Open, Member</div>
          <div className="text-2xl font-semibold">{stats.bySurface.member}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">Open, Internal</div>
          <div className="text-2xl font-semibold">{stats.bySurface.internal}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">Open, Public</div>
          <div className="text-2xl font-semibold">{stats.bySurface.public}</div>
        </Card>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={status} onValueChange={(v) => setStatus(v as Status)}>
          <TabsList>
            <TabsTrigger value="open">Open</TabsTrigger>
            <TabsTrigger value="resolved">Resolved</TabsTrigger>
          </TabsList>
        </Tabs>
        <Select value={surfaceFilter} onValueChange={(v) => setSurfaceFilter(v as any)}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {surfaceOptions.map((o) => (
              <SelectItem key={o.v} value={o.v}>
                {o.l}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={ratingFilter} onValueChange={(v) => setRatingFilter(v as any)}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="down">Down only</SelectItem>
            <SelectItem value="all">All ratings</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading
        </div>
      )}

      <div className="space-y-3">
        {!loading && rows.length === 0 && (
          <Card className="p-6 text-sm text-muted-foreground">
            Nothing here. Feedback will appear as members and agents flag Stew answers.
          </Card>
        )}
        {rows.map((r) => {
          const isOpen = expanded[r.id];
          const answerPreview = r.answer.length > 260 && !isOpen
            ? r.answer.slice(0, 260) + "..."
            : r.answer;
          return (
            <Card key={r.id} className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant={surfaceBadgeVariant(r.surface)} className="capitalize">
                    {r.surface}
                  </Badge>
                  {r.rating === "up" ? (
                    <ThumbsUp className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <ThumbsDown className="h-4 w-4 text-amber-600" />
                  )}
                  <span className="text-xs text-muted-foreground">
                    {new Date(r.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {r.status === "open" ? (
                    <Button
                      size="sm"
                      onClick={() => {
                        setResolveOpen(r);
                        setResolveNote("");
                      }}
                    >
                      Resolve
                    </Button>
                  ) : (
                    <Button size="sm" variant="secondary" onClick={() => reopen(r)} disabled={reopenBusy === r.id}>
                      {reopenBusy === r.id && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                      Reopen
                    </Button>
                  )}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-0.5">
                  Question
                </div>
                <div className="text-sm whitespace-pre-wrap">{r.question}</div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-0.5">
                  Answer
                </div>
                <div className="text-sm whitespace-pre-wrap text-muted-foreground">
                  {answerPreview}
                </div>
                {r.answer.length > 260 && (
                  <button
                    type="button"
                    onClick={() =>
                      setExpanded((prev) => ({ ...prev, [r.id]: !prev[r.id] }))
                    }
                    className="mt-1 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    {isOpen ? (
                      <>
                        <ChevronUp className="h-3 w-3" /> Collapse
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-3 w-3" /> Expand
                      </>
                    )}
                  </button>
                )}
              </div>

              {r.comment && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-0.5">
                    Note
                  </div>
                  <div className="text-sm whitespace-pre-wrap">{r.comment}</div>
                </div>
              )}

              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                  Sources
                </div>
                {r.sources && r.sources.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {r.sources.map((s, i) => (
                      <a
                        key={`${s.doc_id}-${i}`}
                        href={surfaceHref(s)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 rounded-full border bg-muted/40 hover:bg-muted text-muted-foreground hover:text-foreground text-[11px] px-2.5 py-1"
                      >
                        <span className="font-mono">{s.doc_id}</span>
                        {s.title && (
                          <span className="hidden sm:inline">{s.title}</span>
                        )}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground italic">
                    No sources were retrieved, which usually means a content gap.
                  </div>
                )}
              </div>

              {r.status === "resolved" && r.resolution_note && (
                <div className="rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 p-2 text-xs">
                  <span className="font-medium">Resolution:</span> {r.resolution_note}
                </div>
              )}
            </Card>
          );
        })}
      </div>
        </TabsContent>
      </Tabs>



      <Dialog open={!!resolveOpen} onOpenChange={(v) => !v && setResolveOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve feedback</DialogTitle>
          </DialogHeader>
          <Textarea
            value={resolveNote}
            onChange={(e) => setResolveNote(e.target.value)}
            placeholder="What was fixed"
            rows={4}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResolveOpen(null)}>
              Cancel
            </Button>
            <Button onClick={resolve} disabled={busy}>
              {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Mark resolved
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log an improvement</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">Surface</label>
              <Select value={logSurface} onValueChange={(v) => setLogSurface(v as Surface)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="internal">Internal</SelectItem>
                  <SelectItem value="public">Public</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Question or topic</label>
              <Input
                value={logQuestion}
                onChange={(e) => setLogQuestion(e.target.value)}
                placeholder="What was the member asking?"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">What was wrong</label>
              <Textarea
                value={logNote}
                onChange={(e) => setLogNote(e.target.value)}
                rows={3}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Note (optional)</label>
              <Textarea
                value={logComment}
                onChange={(e) => setLogComment(e.target.value)}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={logImprovement}
              disabled={busy || !logQuestion.trim() || !logNote.trim()}
            >
              {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              File
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
